import {
  ReactNode,
  createContext,
  memo,
  useCallback,
  useContext,
  useEffect,
  useState,
} from "react";
import { fetchCurrentUser } from "../api/modules";
import { UserDetailsResponse } from "../types/user";

interface AppContextType {
  user: UserDetailsResponse | null;
  loading: boolean;
  error: string | null;
  refreshUser: () => Promise<void>;
  setUser: (user: UserDetailsResponse | null) => void;
}

const AppContext = createContext<AppContextType | undefined>(undefined);

export const useAppContext = (): AppContextType => {
  const context = useContext(AppContext);
  if (!context) {
    throw new Error("useAppContext must be used within an AppProvider");
  }
  return context;
};

interface AppProviderProps {
  children: ReactNode;
}

export const AppProvider = memo<AppProviderProps>(({ children }) => {
  const [user, setUser] = useState<UserDetailsResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const refreshUser = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      // Only fetch user if token exists
      const token = localStorage.getItem("token");
      if (!token) {
        setUser(null);
        setLoading(false);
        return;
      }

      const response = await fetchCurrentUser();

      // Handle new API response structure: data.data for single user
      const responseData = response.data as any;
      const userData = responseData?.data || responseData;

      // Assuming response.data contains the user details matching the User type
      setUser(userData);
    } catch (err: any) {
      console.error("Error fetching user:", err);
      setError(err?.message || "Failed to fetch user data");
      setUser(null);
    } finally {
      setLoading(false);
    }
  }, []);

  // Initial load - only once
  useEffect(() => {
    refreshUser();
  }, [refreshUser]);

  const contextValue: AppContextType = {
    user,
    loading,
    error,
    refreshUser,
    setUser,
  };

  return (
    <AppContext.Provider value={contextValue}>{children}</AppContext.Provider>
  );
});

AppProvider.displayName = "AppProvider";
